import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import {
  AccountListFilterSelection,
  AccountStatus,
  RestaurantGroup,
  STATUS_OPTIONS,
} from '@/experiences/sales-portal/domain/accounts/screens/account-list/types';
import { Chip, Icons } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_EASING_IN_OUT_CONFIG } from '@experiences/blocs/utils/animations';
import {
  FilterDropdownMenu,
  FilterDropdownOption,
} from '@/experiences/sales-portal/components/filter-dropdown-menu';
import { isWeb } from '@/utils/navigation';
import { View } from 'react-native';

// Diameter of the circular clear chip
const CLEAR_BUTTON_SIZE = 32;

const STATUS_LABEL_KEYS: Record<AccountStatus, string> = {
  [AccountStatus.ACTIVE]: 'sales_portal.account_list.filter.status.active',
  [AccountStatus.NEW]: 'sales_portal.account_list.filter.status.new',
  [AccountStatus.PROSPECT]: 'sales_portal.account_list.filter.status.prospect',
};

type Props = AccountListFilterSelection & {
  groupCounts?: Map<string, number>;
  restaurantGroups: RestaurantGroup[];
  statusCounts?: Map<AccountStatus, number>;
};

const formatAccountCount = (count: number) =>
  i18n.t(
    count === 1
      ? 'sales_portal.account_list.filter.account_count'
      : 'sales_portal.account_list.filter.account_count_plural',
    { count },
  );

export const AccountListFilters: React.FC<Props> = ({
  clearFilters,
  groupCounts,
  hasActiveFilters,
  restaurantGroups,
  selectedGroups,
  selectedStatuses,
  statusCounts,
  toggleGroup,
  toggleStatus,
}) => {
  const [styles, theme] = useStyles();
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isGroupsOpen, setIsGroupsOpen] = useState(false);

  const clearButtonProgress = useDerivedValue(() =>
    withTiming(hasActiveFilters ? 1 : 0, DEFAULT_EASING_IN_OUT_CONFIG),
  );

  const clearButtonStyle = useAnimatedStyle(() => ({
    marginRight: clearButtonProgress.value * theme.sk.spacing.spaceSmall,
    opacity: clearButtonProgress.value,
    transform: [
      {
        translateX:
          (clearButtonProgress.value - 1) *
          (CLEAR_BUTTON_SIZE + theme.sk.spacing.spaceSmall),
      },
    ],
    width: clearButtonProgress.value * CLEAR_BUTTON_SIZE,
  }));

  const hasGroups = restaurantGroups.length > 0;

  // Web rows show the numeric count and disable empty options; the mobile sheet
  // shows the count as a subtitle (countLabel). Groups carry a count for web but
  // no subtitle — the mobile sheet keeps group rows count-free by design.
  // The backend decides which statuses are selectable (e.g. no Prospect for
  // orgs with Prospect Strategist), so options come from its counts.
  const statusDropdownOptions: FilterDropdownOption<AccountStatus>[] =
    Array.from(statusCounts ?? [], ([status, count]) => ({
      count,
      countLabel: formatAccountCount(count),
      disabled: count === 0 && !selectedStatuses.has(status),
      key: status,
      label: i18n.t(STATUS_LABEL_KEYS[status]),
      selected: selectedStatuses.has(status),
    }));

  const hasStatuses = statusDropdownOptions.length > 0;

  const groupDropdownOptions: FilterDropdownOption<string>[] = restaurantGroups
    .map((group) => {
      const count = groupCounts?.get(group.uuid) ?? 0;
      const selected = selectedGroups.has(group.uuid);
      return {
        key: group.uuid,
        label: group.name,
        selected,
        ...(groupCounts ? { count, disabled: count === 0 && !selected } : {}),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedStatus = STATUS_OPTIONS.find((status) =>
    selectedStatuses.has(status),
  );
  const statusChipLabel =
    selectedStatuses.size > 1
      ? i18n.t('sales_portal.account_list.filter.status.selected_count', {
          count: selectedStatuses.size,
        })
      : selectedStatus
        ? i18n.t(STATUS_LABEL_KEYS[selectedStatus])
        : i18n.t('sales_portal.account_list.filter.status');
  const selectedGroup = restaurantGroups.find((group) =>
    selectedGroups.has(group.uuid),
  );
  const groupsChipLabel =
    selectedGroups.size > 1
      ? i18n.t('sales_portal.account_list.filter.groups.selected_count', {
          count: selectedGroups.size,
        })
      : (selectedGroup?.name ??
        i18n.t('sales_portal.account_list.filter.groups'));

  return (
    <View style={styles.container}>
      {/* Web-only clear-X; mobile clears via the sheet's "Clear all" */}
      {isWeb ? (
        <Animated.View
          accessibilityElementsHidden={!hasActiveFilters}
          importantForAccessibility={
            hasActiveFilters ? 'auto' : 'no-hide-descendants'
          }
          pointerEvents={hasActiveFilters ? 'auto' : 'none'}
          style={clearButtonStyle}
        >
          <Chip
            accessibilityLabel={i18n.t(
              'sales_portal.account_list.filter.clear',
            )}
            circular
            height={CLEAR_BUTTON_SIZE}
            onPress={clearFilters}
            pressable
            width={CLEAR_BUTTON_SIZE}
          >
            <Chip.Icon>
              <Icons.X />
            </Chip.Icon>
          </Chip>
        </Animated.View>
      ) : null}

      <View style={styles.filterWrapper}>
        {hasStatuses ? (
          <FilterDropdownMenu
            isOpen={isStatusOpen}
            onOpenChange={setIsStatusOpen}
            onToggle={toggleStatus}
            options={statusDropdownOptions}
            title={i18n.t('sales_portal.account_list.filter.status.title')}
            trigger={
              <Chip
                accessibilityLabel={i18n.t(
                  'sales_portal.account_list.filter.status',
                )}
                circular
                onPress={() => setIsStatusOpen(!isStatusOpen)}
                pressable
                selected={selectedStatuses.size > 0}
              >
                <Chip.Text>{statusChipLabel}</Chip.Text>
                <Chip.Icon>
                  <Icons.ChevronDown />
                </Chip.Icon>
              </Chip>
            }
          />
        ) : null}

        {hasGroups ? (
          <FilterDropdownMenu
            isOpen={isGroupsOpen}
            onOpenChange={setIsGroupsOpen}
            onToggle={toggleGroup}
            options={groupDropdownOptions}
            title={i18n.t('sales_portal.account_list.filter.groups.title')}
            trigger={
              <Chip
                accessibilityLabel={i18n.t(
                  'sales_portal.account_list.filter.groups',
                )}
                circular
                onPress={() => setIsGroupsOpen(!isGroupsOpen)}
                pressable
                selected={selectedGroups.size > 0}
              >
                <Chip.Text>{groupsChipLabel}</Chip.Text>
                <Chip.Icon>
                  <Icons.ChevronDown />
                </Chip.Icon>
              </Chip>
            }
          />
        ) : null}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    flexDirection: 'row',
    paddingBottom: theme.sk.spacing.spaceSmall,
  },
  filterWrapper: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
